import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Copy, Inbox, Undo2, FileText, Users, Calculator, Type } from 'lucide-react';
import {
  Drawer, Modal, Card, Typography, Button, Row, Col, Tag, Space, Divider, Popconfirm, EmptyState, Alert, message, Pagination,
  Form, Field, Input, InputNumber, DatePicker, Select, Switch, StatCard, statTone,
} from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUrlString, useUrlNumber } from '../../hooks/useUrlState';
import { useConfirmedMutation } from '../../lib/confirm';
import dayjs from 'dayjs';
import { request, api } from '../../api/client';
import { requestAllPages } from '../../api/allPages';

async function openSchemePdf(id: string, retailerId?: string) {
  const r = await api.get(`/discount-schemes/${id}/acceptance-pdf`, { params: retailerId ? { retailerId } : {}, responseType: 'blob' });
  window.open(URL.createObjectURL(r.data), '_blank');
}
import { cn } from '../../ui/cn';
import { usePermission } from '../../store/auth';
import { TwDataTable, Pill, type DataTableColumn } from '../../components/TwDataTable';
import { inr2 as inr } from '../../lib/format';
import { stateName } from '../../constants/indianStates';
import { currentFy, fyOptions, fyStartDate } from '../../lib/financialYear';
import { useServerSort } from '../../hooks/useServerSort';

// Lifecycle: DRAFT (parked) → INACTIVE (on the shelf, ready) ⇄ ACTIVE (the single live scheme) ; ARCHIVED (retired).
const STATUS_COLOR: Record<string, string> = { DRAFT: 'gold', INACTIVE: 'blue', ACTIVE: 'green', ARCHIVED: 'default' };
const STATUS_LABEL: Record<string, string> = { DRAFT: 'Draft', INACTIVE: 'Inactive', ACTIVE: 'Active', ARCHIVED: 'Archived' };
const FREQ_LABEL: Record<string, string> = { WEEKLY: 'Weekly', BIWEEKLY: 'Biweekly', MONTHLY: 'Monthly' };
const amountProps = {
  style: { width: '100%' },
  min: 0,
  step: 100000,
  formatter: (v: any) => (v == null || v === '' ? '' : `₹${Number(v).toLocaleString('en-IN')}`),
  parser: (v: any) => Number(String(v ?? '').replace(/[^\d.]/g, '')) as any,
};

// The company's current structure — pre-filled on "New scheme" so it's a starting point to tweak.
const SEED = {
  // Derived, not hardcoded: a literal '2026-27' silently becomes last year's default every April.
  financialYear: currentFy(),
  creditPeriodDays: 90,
  overdueInterestPct: 2,
  remindersEnabled: true,
  reminderFrequency: 'WEEKLY',
  advanceSlabBasis: 'BASIC_TAXABLE_VALUE',
  advanceCdSlabs: [{ minAmount: 500000, cdPct: 7.5 }, { minAmount: 750000, cdPct: 10 }, { minAmount: 1000000, cdPct: 12.5 }],
  timelineCdSlabs: [{ maxDays: 0, cdPct: 10 }, { maxDays: 15, cdPct: 5 }, { maxDays: 30, cdPct: 2.5 }],
  todSlabs: [
    { fromAmount: 0, toAmount: 2000000, basePct: 0, perLakhPct: 1, capPct: 20 },
    { fromAmount: 2000000, toAmount: 3000000, basePct: 20, perLakhPct: 0.5, capPct: 25 },
    { fromAmount: 3000000, toAmount: null, basePct: 25, perLakhPct: 0, capPct: 25 },
  ],
};

export function CommercialTermsPage() {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<any>(null);
  /* Renaming is not amending — see the row action below. Held separately from the edit form, which a
     live scheme's terms are locked out of. */
  const [renameFor, setRenameFor] = useState<any>(null);
  const [renameTo, setRenameTo] = useState('');
  const canManage = usePermission('COMMERCIAL.MANAGE');
  const canAssign = usePermission('COMMERCIAL.ASSIGN');
  const qc = useQueryClient();

  // Sorting is the backend's ORDER BY (see useServerSort) so every caller of this endpoint sees the
  // same order, and the sort survives a reload or a Back out of a row — it lives in the URL.
  // dealerCount is counted by a second server query, so that column is deliberately not sortable.
  const { sortState, onSortChange, sort } = useServerSort();

  const { data, isLoading } = useQuery({
    queryKey: ['commercial-schemes', sort],
    queryFn: () => request<any[]>({ url: '/discount-schemes', params: sort ? { sort } : {} }),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['commercial-schemes'] });
  const act = (url: string, method: 'POST' | 'DELETE', okMsg: string, body?: any) =>
    request({ url, method, data: body }).then(() => { message.success(okMsg); invalidate(); })
      .catch((e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'));

  const columns: DataTableColumn<any>[] = [
    { key: 'name', title: 'Scheme', dataIndex: 'name', sortable: true, alwaysVisible: true,
      render: (v: string, r: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-content">{v}</span>
          {r.version > 1 && <Pill>v{r.version}</Pill>}
        </div>
      ) },
    { key: 'financialYear', title: 'FY', dataIndex: 'financialYear', sortable: true,
      render: (v: string) => <span className="text-muted">{v}</span> },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (v: string) => <Pill color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Pill> },
    { key: 'dealerCount', title: 'Retailers', dataIndex: 'dealerCount', align: 'right',
      render: (v: number) => <span className="tabular-nums">{v || 0}</span> },
    { key: 'reminders', title: 'Reminders', dataIndex: 'remindersEnabled',
      render: (_: any, r: any) => (r.remindersEnabled
        ? <Pill color="green">{FREQ_LABEL[r.reminderFrequency] ?? 'Weekly'}</Pill>
        : <span className="text-subtle">Off</span>) },
    { key: 'effectiveFrom', title: 'From', dataIndex: 'effectiveFrom', sortable: true,
      render: (v: any) => <span className="tabular-nums text-muted">{v}</span> },
    { key: 'effectiveTo', title: 'To', dataIndex: 'effectiveTo', sortable: true,
      render: (v: any) => v ? <span className="tabular-nums text-muted">{v}</span> : <Pill color="green">Open</Pill> },
    { key: 'creditPeriodDays', title: 'Credit', dataIndex: 'creditPeriodDays', align: 'right', sortable: true,
      render: (v: any, r: any) => {
        const pct = Number(r.overdueInterestPct ?? 0);
        return (
          <div className="text-right leading-tight">
            <span className="tabular-nums">{v}d</span>
            {pct > 0 && <div className="text-2xs text-subtle tabular-nums">{pct}%/mo overdue</div>}
          </div>
        );
      } },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, alwaysVisible: true,
      render: (v: any) => v
        ? <span className="tabular-nums text-muted" title={dayjs(v).format('DD MMM YYYY, HH:mm')}>{dayjs(v).format('DD MMM YYYY')}</span>
        : <span className="text-subtle">—</span> },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 280,
      render: (_: any, r: any) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Space wrap size={4}>
            {/* Live toggle FIRST (shelf rows only): flips the single live scheme INACTIVE⇄ACTIVE. Default (not small)
                size so it stands taller than the icon buttons and reads as the primary row control. */}
            {canManage && (r.status === 'ACTIVE' || r.status === 'INACTIVE') && (
              <Switch
                checkedChildren="Live" unCheckedChildren="Off" checked={r.status === 'ACTIVE'} className="mr-1"
                title={r.status === 'ACTIVE' ? 'Live scheme — turn off to shelve it' : 'Turn on to make this the single live scheme'}
                onCheckedChange={(on) => act(`/discount-schemes/${r.id}/status`, 'POST', on ? 'Activated — now the live scheme' : 'Deactivated', { status: on ? 'ACTIVE' : 'INACTIVE' })}
              />
            )}
            <Button variant="text" size="sm" icon={<FileText size={16} />} title="Download PDF" aria-label="Download PDF"
              onClick={() => openSchemePdf(r.id).catch(() => message.error('Failed to open PDF'))} />
            {canManage && (
              <Button variant="text" size="sm" icon={<Pencil size={16} />} title="Edit" aria-label="Edit" onClick={() => { setEditingId(r.id); setOpen(true); }} />
            )}
            {/* RENAME, allowed even while live. The edit form is locked on an active scheme because
                its slabs and credit period are terms dealers accepted; the NAME is only the label it
                is filed under, and a live scheme carrying the wrong one had no way back short of
                deactivating the terms 227 dealers are on. */}
            {canManage && (
              <Button variant="text" size="sm" icon={<Type size={16} />} title="Rename (the label only — terms stay as they are)"
                aria-label={`Rename ${r.name}`} onClick={() => { setRenameFor(r); setRenameTo(r.name); }} />
            )}
            {/* Draft/Archive take a shelf row off the shelf; off-shelf rows (DRAFT / ARCHIVED, via their KPI) get Restore. */}
            {canManage && (r.status === 'ACTIVE' || r.status === 'INACTIVE') && (
              <>
                <Button variant="text" size="sm" icon={<FileText size={16} />} title="Move to draft (park it off the shelf)" aria-label="Move to draft (park it off the shelf)"
                  onClick={() => act(`/discount-schemes/${r.id}/status`, 'POST', 'Moved to draft', { status: 'DRAFT' })} />
                <Button variant="text" size="sm" icon={<Inbox size={16} />} title="Archive (retire it off the shelf)" aria-label="Archive (retire it off the shelf)"
                  onClick={() => act(`/discount-schemes/${r.id}/status`, 'POST', 'Archived', { status: 'ARCHIVED' })} />
              </>
            )}
            {canManage && (r.status === 'DRAFT' || r.status === 'ARCHIVED') && (
              <Button variant="ghost" size="sm" icon={<Undo2 size={16} />} title="Restore to the shelf (Inactive)"
                onClick={() => act(`/discount-schemes/${r.id}/status`, 'POST', 'Restored to the shelf', { status: 'INACTIVE' })}>Restore</Button>
            )}
            {canManage && (
              <Button variant="text" size="sm" icon={<Copy size={16} />} title="Clone to a new draft" aria-label="Clone to a new draft" onClick={() => act(`/discount-schemes/${r.id}/clone`, 'POST', 'Cloned to a new draft')} />
            )}
            {canAssign && (
              <Button variant="text" size="sm" icon={<Users size={16} />} title="Assign retailers" aria-label="Assign retailers" onClick={() => setAssignFor(r)} />
            )}
            {canManage && (
              <Popconfirm
                title={r.status === 'ACTIVE' ? 'Delete this LIVE scheme?' : 'Delete this scheme?'}
                description={r.status === 'ACTIVE' ? 'It will be archived and removed (recoverable).' : undefined}
                okText="Delete" okButtonProps={{ danger: true }}
                onConfirm={() => act(`/discount-schemes/${r.id}`, 'DELETE', 'Deleted')}>
                <Button variant="text" size="sm" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} title="Delete" aria-label="Delete" />
              </Popconfirm>
            )}
          </Space>
        </div>
      ),
    },
  ];

  const schemes = data ?? [];
  const activeCount = schemes.filter((s: any) => s.status === 'ACTIVE').length;
  const inactiveCount = schemes.filter((s: any) => s.status === 'INACTIVE').length;
  const draftCount = schemes.filter((s: any) => s.status === 'DRAFT').length;
  const archivedCount = schemes.filter((s: any) => s.status === 'ARCHIVED').length;
  const shelfCount = activeCount + inactiveCount; // the main list = schemes on the shelf (live + ready)
  const dealersCovered = schemes.reduce((n: number, s: any) => n + Number(s.dealerCount || 0), 0);

  // Click a KPI to filter the table; click it again (or the "Schemes" total) to return to the shelf.
  // Default ('ALL') = the shelf (ACTIVE + INACTIVE); Drafts/Archived are off-shelf, reached only via their KPI.
  const [filter, setFilter] = useUrlString<'ALL' | 'ACTIVE' | 'DRAFT' | 'ARCHIVED' | 'DEALERS'>('shelf', 'ALL');
  const toggle = (f: 'ACTIVE' | 'DRAFT' | 'ARCHIVED' | 'DEALERS') => setFilter((cur) => (cur === f ? 'ALL' : f));
  const filtered = schemes.filter((s: any) =>
    filter === 'ACTIVE' ? s.status === 'ACTIVE'
      : filter === 'DRAFT' ? s.status === 'DRAFT'
        : filter === 'ARCHIVED' ? s.status === 'ARCHIVED'
          : filter === 'DEALERS' ? Number(s.dealerCount || 0) > 0
            : (s.status === 'ACTIVE' || s.status === 'INACTIVE')); // 'ALL' = the shelf

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="type-page-title text-content">Commercial Terms &amp; Discount Structure</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canManage ? (
            <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => { setEditingId(null); setOpen(true); }}>New scheme</Button>
          ) : undefined}
        </div>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-muted">
        Retailer cash discount (advance &amp; prompt-payment), credit terms and turnover discount, per financial year. Distinct from product promo schemes.
      </p>

      <div className="mb-4 mt-4 grid max-w-3xl grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Tile label="On shelf" value={shelfCount} active={filter === 'ALL'} onClick={() => setFilter('ALL')} />
        <Tile label="Active" value={activeCount} accent="text-brand-700" active={filter === 'ACTIVE'} onClick={() => toggle('ACTIVE')} />
        <Tile label="Drafts" value={draftCount} accent="text-harvest-700" active={filter === 'DRAFT'} onClick={() => toggle('DRAFT')} />
        <Tile label="Archived" value={archivedCount} accent="text-muted" active={filter === 'ARCHIVED'} onClick={() => toggle('ARCHIVED')} />
        <Tile label="Retailers covered" value={dealersCovered} accent="text-sky-600" active={filter === 'DEALERS'} onClick={() => toggle('DEALERS')} />
      </div>

      <TwDataTable
        storageKey="commercial-schemes"
        rowKey="id"
        loading={isLoading}
        dataSource={filtered}
        columns={columns}
        sortState={sortState}
        onSortChange={onSortChange}
        // The row opens the editor, matching the pencil in its actions. Only for COMMERCIAL.MANAGE
        // holders: a viewer clicking a row that silently does nothing is worse than a row that never
        // looked clickable, so the pointer cursor is withheld too. The actions cell already stops
        // propagation, so its buttons keep working without triggering the row.
        onRow={(r: any) => ({
          onClick: canManage ? () => { setEditingId(String(r.id)); setOpen(true); } : undefined,
          style: { cursor: canManage ? 'pointer' : 'default' },
        })}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        emptyText={filter === 'ALL'
          ? 'No schemes on the shelf — create one, or restore one from Drafts / Archived.'
          : filter === 'DRAFT' ? 'No drafts — parked schemes show here. Restore one to put it back on the shelf.'
            : filter === 'ARCHIVED' ? 'No archived schemes — retired schemes show here. Restore one to bring it back.'
              : 'No schemes match this filter.'}
        toolbarLeft={
          <span className="text-sm text-muted">
            {filter === 'ALL'
              ? `${shelfCount} on the shelf${activeCount ? ' · 1 live' : ' · none live'}`
              : <>Showing <b className="text-content">{filtered.length}</b> of {schemes.length} · <button className="text-brand-700 hover:underline dark:text-brand-400" onClick={() => setFilter('ALL')}>back to shelf</button></>}
          </span>
        }
      />

      <SchemeBuilder open={open} schemeId={editingId} onClose={() => { setOpen(false); setEditingId(null); }} onSaved={invalidate} />
      <AssignRetailersModal scheme={assignFor} onClose={() => setAssignFor(null)} onChanged={invalidate} />

      {/* The label, on its own — deliberately not the scheme form, which would invite a terms edit
          the server refuses on a live scheme. */}
      <Modal
        open={!!renameFor}
        onOpenChange={(o) => { if (!o) setRenameFor(null); }}
        title="Rename scheme"
        description={renameFor ? `Currently "${renameFor.name}"` : ''}
        width={480}
        footer={
          <>
            <Button variant="default" onClick={() => setRenameFor(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!renameTo.trim() || renameTo.trim() === renameFor?.name}
              onClick={() => {
                const id = renameFor.id;
                setRenameFor(null);
                act(`/discount-schemes/${id}/rename`, 'POST', 'Renamed', { name: renameTo.trim() });
              }}
            >
              Save the name
            </Button>
          </>
        }
      >
        <Field label="Scheme name" hint="Only the name changes — the slabs, credit period and every dealer on it stay exactly as they are.">
          <Input value={renameTo} onChange={(e) => setRenameTo(e.target.value)} maxLength={160} placeholder="e.g. Dealer credit terms 2026-27" />
        </Field>
      </Modal>
    </div>
  );
}

/** Delegates to the shared StatCard. The old version was a bare <button>, which sizes to fit-content
 *  rather than stretching — so this filter row rendered at five different widths. */
function Tile({ label, value, accent = 'text-content', active = false, onClick }: { label: string; value: number; accent?: string; active?: boolean; onClick?: () => void }) {
  return (
    <StatCard
      label={label}
      value={value}
      tone={statTone(accent)}
      onClick={onClick}
      active={active}
    />
  );
}

// ─────────────────────────── Builder ───────────────────────────

function SchemeBuilder({ open, schemeId, onClose, onSaved }: { open: boolean; schemeId: string | null; onClose: () => void; onSaved: () => void }) {
  const [form] = Form.useForm();
  const isEdit = !!schemeId;

  const { data: existing } = useQuery({
    queryKey: ['commercial-scheme', schemeId],
    queryFn: () => request<any>({ url: `/discount-schemes/${schemeId}` }),
    enabled: open && !!schemeId,
  });
  // Retailer list for the inline "Assigned retailers" picker.
  const { data: retailerList } = useQuery({
    queryKey: ['retailers-picker'],
    queryFn: async () => {
      const r: any = await requestAllPages('/retailers');
      return Array.isArray(r) ? r : (r?.data ?? []);
    },
    enabled: open,
  });

  // Seed through `initialValues` + `key`, NOT setFieldsValue-in-an-effect. The dialog body unmounts
  // on close, so the Form remounts on every open and re-seeds its store from `initialValues` during
  // RENDER. Radix mounts that content in a LATER commit than the one where an open-gated effect
  // fires, so the old effect wrote the defaults and the remount then wiped them straight back to {} —
  // which is why every field, both slab lists and the TOD bands came up blank on "New scheme", and
  // why the preview calculator (which posts the live form) always returned 0%.
  const initialValues = useMemo(() => {
    // Effective-from defaults to the 1 April that OPENS the seeded financial year, so the two fields
    // can't disagree the moment the form appears.
    if (!isEdit) return { effectiveFrom: dayjs(fyStartDate(SEED.financialYear)), assignedRetailerIds: [], ...SEED };
    if (!existing) return undefined; // still loading — the key below remounts us once it lands
    return {
      name: existing.name, financialYear: existing.financialYear,
      effectiveFrom: existing.effectiveFrom ? dayjs(existing.effectiveFrom) : null,
      effectiveTo: existing.effectiveTo ? dayjs(existing.effectiveTo) : null,
      creditPeriodDays: existing.creditPeriodDays, overdueInterestPct: Number(existing.overdueInterestPct ?? 0), notes: existing.notes,
      remindersEnabled: existing.remindersEnabled === undefined ? true : !!existing.remindersEnabled,
      reminderFrequency: existing.reminderFrequency ?? 'WEEKLY',
      advanceSlabBasis: existing.advanceSlabBasis ?? 'BASIC_TAXABLE_VALUE',
      advanceCdSlabs: (existing.advanceCdSlabs ?? []).map((s: any) => ({ minAmount: Number(s.minAmount), cdPct: Number(s.cdPct) })),
      timelineCdSlabs: (existing.timelineCdSlabs ?? []).map((s: any) => ({ maxDays: s.maxDays, cdPct: Number(s.cdPct) })),
      todSlabs: (existing.todSlabs ?? []).map((s: any) => ({ fromAmount: Number(s.fromAmount), toAmount: s.toAmount != null ? Number(s.toAmount) : null, basePct: Number(s.basePct), perLakhPct: Number(s.perLakhPct), capPct: Number(s.capPct) })),
      // Pre-fill the current assignment set so saving reconciles to it (no accidental removal).
      assignedRetailerIds: (existing.assignments ?? []).map((a: any) => String(a.retailerId)),
    };
  }, [isEdit, existing]);

  // Remount the Form (re-running the seed) when the target changes, and once the edit payload lands.
  const formKey = isEdit ? `edit-${schemeId}-${existing ? 'loaded' : 'loading'}` : 'create';

  const save = useConfirmedMutation({ title: "Save this scheme?", content: "Active schemes apply to orders as they are placed.", okText: "Yes, save" }, {
    mutationFn: (vals: any) => {
      const payload = {
        name: vals.name, financialYear: vals.financialYear,
        effectiveFrom: vals.effectiveFrom?.format('YYYY-MM-DD'),
        effectiveTo: vals.effectiveTo ? vals.effectiveTo.format('YYYY-MM-DD') : null,
        creditPeriodDays: vals.creditPeriodDays ?? 0, overdueInterestPct: vals.overdueInterestPct ?? 0, notes: vals.notes,
        remindersEnabled: vals.remindersEnabled ?? true, reminderFrequency: vals.reminderFrequency ?? 'WEEKLY',
        advanceSlabBasis: vals.advanceSlabBasis ?? 'BASIC_TAXABLE_VALUE',
        advanceCdSlabs: (vals.advanceCdSlabs ?? []).filter((s: any) => s?.minAmount != null),
        timelineCdSlabs: (vals.timelineCdSlabs ?? []).filter((s: any) => s?.maxDays != null),
        todSlabs: (vals.todSlabs ?? []).filter((s: any) => s?.fromAmount != null).map((s: any) => ({ ...s, toAmount: s.toAmount ?? null })),
        // Reconcile assignments to exactly this list. Only send when the field is initialised (avoids
        // wiping assignments if the form is submitted before it loads on edit).
        // The blank filter is not paranoia: an empty entry here reached MySQL as
        // `Incorrect integer value: '' for column retailer_id` and broke Create scheme outright.
        ...(Array.isArray(vals.assignedRetailerIds)
          ? { assignedRetailerIds: vals.assignedRetailerIds.map(String).filter((id: string) => id.trim() !== '') }
          : {}),
      };
      return isEdit ? request({ url: `/discount-schemes/${schemeId}`, method: 'PUT', data: payload }) : request({ url: '/discount-schemes', method: 'POST', data: payload });
    },
    onSuccess: () => { message.success(isEdit ? 'Scheme updated' : 'Scheme created — added to the shelf'); onSaved(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal
      // A wide CENTRED dialog rather than a side panel: this form is six numbered sections and the
      // TOD block alone is a five-column grid, which the 920px drawer could only show by scrolling
      // sideways. maskClosable={false} because a stray backdrop click would bin a long form.
      open={open} onOpenChange={(o) => { if (!o) onClose(); }} width={1160}
      maskClosable={false}
      title={isEdit ? `Edit scheme${existing?.status ? ` · ${STATUS_LABEL[existing.status] ?? existing.status}` : ''}` : 'New commercial scheme'}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>{isEdit ? 'Save changes' : 'Create scheme'}</Button></>}
    >
      <Form key={formKey} form={form} initialValues={initialValues} layout="vertical" onFinish={(v) => {
        // Auto-arrange the TOD bands (sort by From + snap contiguous upper bounds) so the user needn't order them
        // by hand. The reorder is written back to the form so it's visible; a genuine same-lower-bound clash stops
        // the save with a precise message.
        const tidy = tidyTodBands(v.todSlabs);
        form.setFieldValue('todSlabs', tidy.todSlabs);
        if (tidy.error) { message.error(tidy.error); return; }
        save.mutate({ ...v, todSlabs: tidy.todSlabs });
      }}>
        {isEdit && existing?.status === 'ACTIVE' && (
          <Alert type="warning" className="mb-3"
            title="Editing a live scheme"
            description="Changes apply to future discounts & reminders only — past discount adjustments are unaffected. To keep the current version intact, use Clone instead." />
        )}
        <Row gutter={[12, 12]}>
          <Col xs={24} md={7}><Form.Item name="name" label="Scheme name" rules={[{ required: true }]}><Input placeholder={`Retailer Discount Structure FY ${currentFy()}`} /></Form.Item></Col>
          {/* A list, not a text box: the year is parsed downstream to work out the FY's date range, and
              free text let "2026-27ghgfh" through — which would have dropped the scheme out of the
              year-end turnover-bonus run. An existing scheme's own value is always offered (see
              fyOptions `include`) so editing an old row can't quietly rewrite its year. */}
          <Col xs={8} md={5}><Form.Item name="financialYear" label="Financial year" rules={[{ required: true }]}>
            <Select showSearch options={fyOptions({ include: existing?.financialYear })} placeholder="Select year" />
          </Form.Item></Col>
          {/* span 6, not 4: a date field has a hard ~156px floor (min-w-[9rem] plus the width its own
              formatted value needs), so a narrower column left the button hanging out past the grid
              and put a horizontal scrollbar on the dialog body. */}
          <Col xs={8} md={6}><Form.Item name="effectiveFrom" label="Effective from" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          <Col xs={8} md={6}><Form.Item name="effectiveTo" label="Effective to"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
        </Row>

        <Divider orientation="left">1. Advance / prepaid cash discount</Divider>
        <Typography.Text type="secondary">Full payment <b>before dispatch</b> — discount by the amount paid.</Typography.Text>
        <Row gutter={[12, 12]} style={{ marginTop: 8, marginBottom: 4 }}>
          <Col span={12}>
            <Form.Item name="advanceSlabBasis" label="Match slabs against"
              tooltip="Which order value the prepaid amount slabs above are compared against. Company policy = the basic / taxable value (before GST).">
              <Select options={[
                { value: 'BASIC_TAXABLE_VALUE', label: 'Basic / taxable value (before GST) — recommended' },
                { value: 'GRAND_TOTAL', label: 'Grand total (GST-inclusive)' },
              ]} />
            </Form.Item>
          </Col>
        </Row>
        <SlabList name="advanceCdSlabs" cols={[
          { name: 'minAmount', label: 'Prepaid amount ≥', render: () => <InputNumber {...amountProps} /> },
          { name: 'cdPct', label: 'Discount %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" /> },
        ]} add={{ minAmount: 500000, cdPct: 5 }} />

        <Divider orientation="left">2. Prompt-payment cash discount</Divider>
        <Typography.Text type="secondary">Paid <b>after delivery</b> — discount by days from invoice date (0 = before/at billing).</Typography.Text>
        <SlabList name="timelineCdSlabs" cols={[
          { name: 'maxDays', label: 'Paid within (days) ≤', render: () => <InputNumber style={{ width: '100%' }} min={0} max={3650} /> },
          { name: 'cdPct', label: 'Discount %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" /> },
        ]} add={{ maxDays: 45, cdPct: 0 }} />

        <Divider orientation="left">3. Credit terms</Divider>
        <Row gutter={[12, 12]}>
          <Col span={8}><Form.Item name="creditPeriodDays" label="Credit period (days)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={3650} /></Form.Item></Col>
          <Col span={8}><Form.Item name="overdueInterestPct" label="Overdue interest (% / month)" rules={[{ required: true }]}><InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} addonAfter="%" /></Form.Item></Col>
        </Row>

        <Divider orientation="left">4. Turnover discount (TOD)</Divider>
        <Typography.Text type="secondary">Year-end, on annual turnover (per completed lakh). Enter each band's <b>From</b> + rates in any order — on save the bands are auto-sorted and their upper bounds snapped to be contiguous (last band open-ended). Two bands can't start at the same amount.</Typography.Text>
        <SlabList name="todSlabs" wide cols={[
          { name: 'fromAmount', label: 'From (₹)', render: () => <InputNumber {...amountProps} /> },
          { name: 'toAmount', label: 'To (auto · blank = ∞)', render: () => <InputNumber {...amountProps} /> },
          { name: 'basePct', label: 'Base %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} /> },
          { name: 'perLakhPct', label: '% / lakh', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} /> },
          { name: 'capPct', label: 'Cap %', render: () => <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.5} /> },
        ]} add={{ fromAmount: 3000000, toAmount: null, basePct: 25, perLakhPct: 0, capPct: 25 }} />

        <Divider orientation="left">5. Prompt-payment reminders</Divider>
        <Typography.Text type="secondary">Nudge assigned retailers about invoices still inside the cash-discount window — so they pay early and claim the discount. Only retailers with a live opportunity are notified.</Typography.Text>
        <Row gutter={[12, 12]} style={{ marginTop: 10 }} align="bottom">
          <Col span={8}>
            <Form.Item name="remindersEnabled" label="Send reminders to retailers" valuePropName="checked">
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="reminderFrequency" label="Frequency">
              <Select options={[
                { value: 'WEEKLY', label: 'Weekly' },
                { value: 'BIWEEKLY', label: 'Every 2 weeks' },
                { value: 'MONTHLY', label: 'Monthly' },
              ]} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">6. Assigned retailers</Divider>
        <Typography.Text type="secondary">The retailers this scheme applies to. Cash discounts (advance &amp; prompt-payment), credit terms and TOD only apply to <b>assigned</b> retailers. Editing here adds/removes assignments on save.</Typography.Text>
        <Form.Item name="assignedRetailerIds" style={{ marginTop: 10 }}>
          <RetailerPicker retailers={retailerList ?? []} />
        </Form.Item>
        <Typography.Text type="secondary" className="text-xs">Tip: use the “Retailers” action on the scheme list to manage this scheme’s retailers or download its PDF.</Typography.Text>

        <Form.Item name="notes" label="Notes" style={{ marginTop: 12 }}><Input.TextArea rows={2} maxLength={2000} placeholder="General terms, remarks…" /></Form.Item>

        <PreviewPanel form={form} />
      </Form>
    </Modal>
  );
}

// TOD bands partition the turnover axis by their LOWER bound (computeTod only ever uses `From`), so the upper
// bound is really just "the next band's lower bound". On save we therefore auto-sort the bands by From and snap
// each upper bound to the next band's lower bound (the last band stays open-ended). This lets the user enter
// bands in any order and never hand-align the To column. The one thing we can't resolve automatically is two
// bands sharing a lower bound (which one wins is ambiguous) — that is surfaced as a precise error.
function tidyTodBands(rows: any[]): { todSlabs: any[]; error?: string } {
  const raw = (rows ?? []).filter((s: any) => s?.fromAmount != null && s.fromAmount !== '');
  if (!raw.length) return { todSlabs: [] };
  const sorted = [...raw].sort((a, b) => Number(a.fromAmount) - Number(b.fromAmount));
  for (let i = 1; i < sorted.length; i++) {
    if (Number(sorted[i].fromAmount) === Number(sorted[i - 1].fromAmount)) {
      return { todSlabs: sorted, error: `Two TOD bands start at ₹${Number(sorted[i].fromAmount).toLocaleString('en-IN')}. Each band must start higher than the one before — remove or change one.` };
    }
  }
  const snapped = sorted.map((s: any, i: number) => ({ ...s, toAmount: i < sorted.length - 1 ? Number(sorted[i + 1].fromAmount) : null }));
  return { todSlabs: snapped };
}

function SlabList({ name, cols, add, wide }: { name: string; cols: { name: string; label: string; render: () => any }[]; add: any; wide?: boolean }) {
  const span = Math.max(2, Math.floor((wide ? 22 : 20) / cols.length));
  return (
    <Form.List name={name}>
      {(fields, { add: addRow, remove }) => (
        <div>
          {fields.length > 0 && (
            <Row gutter={[12, 12]} style={{ marginBottom: 2 }}>
              {cols.map((c) => <Col key={c.name} span={span}><Typography.Text type="secondary" className="text-xs">{c.label}</Typography.Text></Col>)}
            </Row>
          )}
          {fields.map(({ key, name: fname }) => (
            <Row key={key} gutter={[12, 12]} style={{ marginBottom: 6 }} align="middle">
              {cols.map((c) => (
                <Col key={c.name} span={span}>
                  <Form.Item name={[fname, c.name]} noStyle>{c.render()}</Form.Item>
                </Col>
              ))}
              <Col span={2}><Button variant="danger" size="sm" icon={<Trash2 size={16} />} onClick={() => remove(fname)} aria-label="Remove row" /></Col>
            </Row>
          ))}
          <Button variant="default" onClick={() => addRow(add)} block icon={<Plus size={16} />} style={{ marginTop: 4 }}>Add row</Button>
        </div>
      )}
    </Form.List>
  );
}

/** "No filter" as a selectable option. Never '' — a blank in a retailer field is a real bug source. */
const ALL = '__all__';

/** The levels worth showing, outermost first. Anything above a state is the same for everyone. */
const PLACE_LEVELS = ['state', 'district', 'beat'];

/** One colour per level, so the trail reads as a hierarchy rather than three similar tags. */
const PLACE_COLOR: Record<string, string> = {
  state: 'blue',
  district: 'gold',
  beat: 'green',
};

/**
 * Retailer multi-select with State / Territory narrowing and bulk select.
 *
 * The retailer list runs to a few hundred rows across dozens of territories, so picking a whole
 * region by hand was the slow part. The two dropdowns narrow the list, and "Select all" then takes
 * exactly what the filters are showing — so "every retailer in Maharashtra" is two clicks.
 *
 * Filter choices are derived from the retailers themselves (never a static list), so a territory with
 * no retailers is never offered, and choosing a state narrows the territory list to that state.
 *
 * Drop-in for Form.Item: takes `value` / `onChange` like the Select it replaces.
 */
function RetailerPicker({ value, onChange, retailers }: { value?: string[]; onChange?: (v: string[]) => void; retailers: any[] }) {
  // "All" is a real, selected option rather than an empty placeholder, so the filters always read as
  // a definite state ("All states") instead of looking unset. A sentinel — not '' — because a blank
  // value in a retailer field is exactly what broke Create scheme (see normalizeRetailerIds).
  const [stateCode, setStateCode] = useUrlString<string>('state', ALL);
  const [territoryId, setTerritoryId] = useUrlString<string>('terr', ALL);

  const { data: territories } = useQuery({
    queryKey: ['territories'],
    queryFn: () => request<any[]>({ url: '/territories' }),
  });
  const terrName = useMemo(
    () => new Map((territories ?? []).map((t: any) => [String(t.id), t.name as string])),
    [territories],
  );
  // The whole node, so a retailer's beat can be walked UP to its district and state. Retailers are
  // attached at whichever level fits them — most at district, some at a beat — so the depth of the
  // trail varies per retailer and has to be derived rather than assumed.
  const terrNode = useMemo(
    () => new Map<string, any>((territories ?? []).map((t: any) => [String(t.id), t])),
    [territories],
  );

  /** The territory trail for a retailer, ordered state → district → beat (whatever exists). */
  const placeOf = useMemo(() => (territoryId?: string | null) => {
    const out: { level: string; name: string }[] = [];
    let node = territoryId ? terrNode.get(String(territoryId)) : null;
    const guard = new Set<string>();               // parent loops in bad data must not hang the picker
    while (node && !guard.has(String(node.id))) {
      guard.add(String(node.id));
      // Only the levels that actually locate a retailer. The hierarchy has a single root zone
      // ("India") above the states, and stamping it on all 300 rows says nothing about any of them.
      const lvl = String(node.level ?? '');
      if (node.name && PLACE_LEVELS.includes(lvl)) out.unshift({ level: lvl, name: String(node.name) });
      node = node.parentId ? terrNode.get(String(node.parentId)) : null;
    }
    return out;
  }, [terrNode]);

  const all = retailers ?? [];
  // Tolerate a non-array / blank-bearing value: the field has been seen holding a bare '' (which the
  // multi-select then drew as a nameless chip, and the save posted straight into a BIGINT column).
  const selected = useMemo(
    () => (Array.isArray(value) ? value : value == null ? [] : [value]).map(String).filter((v) => v.trim() !== ''),
    [value],
  );

  // Only offer states/territories that actually HAVE retailers — an empty filter choice is just a dead end.
  // stateName() humanises the GST code and falls back to the raw code for bad legacy data (e.g. "MH").
  const stateOptions = useMemo(() => {
    const codes = [...new Set(all.map((d: any) => String(d.stateCode ?? '')).filter(Boolean))];
    const named = codes.map((c) => ({ value: c, label: stateName(c) })).sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: ALL, label: `All states (${all.length})` }, ...named];
  }, [all]);

  const territoryOptions = useMemo(() => {
    const pool = all.filter((d: any) => stateCode === ALL || String(d.stateCode) === stateCode);
    const ids = [...new Set(pool.map((d: any) => String(d.territoryId ?? '')).filter(Boolean))];
    const named = ids.map((id) => ({ value: id, label: terrName.get(id) ?? `Territory ${id}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // Scoped to the chosen state, so name it — "All territories" under Maharashtra means all 27 of ITS
    // territories, not all 59.
    const label = stateCode === ALL ? `All territories (${named.length})` : `All in ${stateName(stateCode)} (${named.length})`;
    return [{ value: ALL, label }, ...named];
  }, [all, stateCode, terrName]);

  // Picking a state can strand a territory from a different one — drop it rather than filter to nothing.
  useEffect(() => {
    if (territoryId !== ALL && !territoryOptions.some((o) => o.value === territoryId)) setTerritoryId(ALL);
  }, [territoryOptions, territoryId]);

  const matching = useMemo(() => all.filter((d: any) =>
    (stateCode === ALL || String(d.stateCode) === stateCode)
    && (territoryId === ALL || String(d.territoryId) === territoryId)),
  [all, stateCode, territoryId]);

  const labelOf = (d: any) => `${d.companyName}${d.code ? ` (${d.code})` : ''}`;

  // Selected retailers that the current filter excludes are KEPT in the options list — otherwise their
  // chips would lose their labels and show a bare row id the moment you narrowed the filter.
  const options = useMemo(() => {
    const shown = new Set(matching.map((d: any) => String(d.id)));
    const strandedSelections = all.filter((d: any) => selected.includes(String(d.id)) && !shown.has(String(d.id)));
    return [...matching, ...strandedSelections].map((d: any) => ({
      value: String(d.id),
      label: labelOf(d),
      // Where this retailer sits, shown as capsules on the dropdown row (never on the selected chip).
      place: placeOf(d.territoryId),
      // Typing a town, territory or state in the dropdown search should find its retailers too.
      hay: `${d.companyName ?? ''} ${d.code ?? ''} ${d.city ?? ''} ${terrName.get(String(d.territoryId)) ?? ''} ${stateName(d.stateCode)}`.toLowerCase(),
    }));
  }, [matching, all, selected, terrName, placeOf]);

  const matchingIds = useMemo(() => matching.map((d: any) => String(d.id)), [matching]);
  const toAdd = matchingIds.filter((id) => !selected.includes(id));
  const filtered = stateCode !== ALL || territoryId !== ALL;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Select
          showSearch value={stateCode} onChange={(v: any) => setStateCode(v ?? ALL)}
          options={stateOptions} style={{ width: 200 }} aria-label="Filter retailers by state"
        />
        <Select
          showSearch value={territoryId} onChange={(v: any) => setTerritoryId(v ?? ALL)}
          options={territoryOptions} style={{ width: 240 }} aria-label="Filter retailers by territory"
        />
        <Button size="sm" variant="default" disabled={!toAdd.length}
          onClick={() => onChange?.([...selected, ...toAdd])}>
          {filtered ? `Select these ${toAdd.length}` : `Select all ${toAdd.length}`}
        </Button>
        <Button size="sm" variant="text" disabled={!selected.length} onClick={() => onChange?.([])}>Clear</Button>
        <span className="type-caption text-subtle">
          {filtered ? `${matching.length} of ${all.length} retailers shown` : `${all.length} retailers`}
          {selected.length ? ` · ${selected.length} selected` : ''}
        </span>
      </div>
      <Select
        mode="multiple" allowClear showSearch style={{ width: '100%' }}
        // Now that one click can select several hundred retailers, cap the chips — the count above the
        // field is the honest read-out, and 300 chips would push the rest of the form off-screen.
        maxTagCount={12}
        value={selected} onChange={(v: any) => onChange?.((v ?? []).map(String).filter((id: string) => id.trim() !== ''))}
        placeholder="Select retailers this scheme applies to…"
        options={options}
        filterOption={(input: string, option: any) => String(option?.hay ?? '').includes(input.toLowerCase())}
        // Only the dropdown ROW gets the location trail — the selected chips keep the plain name, or a
        // few hundred selections would each carry three capsules.
        optionRender={(o: any) => (
          <span className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="truncate">{o.label}</span>
            {(o.place ?? [])
              // A chosen state is already the answer to "which state" — repeating it on every row is noise.
              .filter((p: any) => !(stateCode !== ALL && p.level === 'state'))
              .map((p: any) => (
                <Pill key={`${p.level}-${p.name}`} color={PLACE_COLOR[p.level] ?? 'default'}>{p.name}</Pill>
              ))}
          </span>
        )}
      />
    </div>
  );
}

/** One scenario input: a labelled control with the plain-English meaning underneath. */
function ScenarioField({ label, hint, children }: { label: string; hint: string; children: any }) {
  return (
    <div>
      <div className="type-label text-content">{label}</div>
      {children}
      <div className="type-caption mt-1 text-subtle">{hint}</div>
    </div>
  );
}

/**
 * One computed figure: what it is, what it comes to, and WHY — the `rule` sentence comes from the
 * backend (see CommercialSchemeService.preview) rather than being re-derived here, so the wording
 * can never drift away from the number it is explaining.
 */
function ResultCard({ name, jargon, value, worth, rule, muted }: {
  name: string; jargon?: string; value: string; worth?: string | null; rule?: string; muted?: boolean;
}) {
  return (
    <div className="rounded-card border border-line bg-surface shadow-e1 p-3">
      <div className="type-label-strong text-muted">{name}</div>
      {jargon && <div className="type-caption text-subtle">{jargon}</div>}
      <div className={cn('type-metric mt-1.5', muted ? 'text-subtle' : 'text-brand-700')}>{value}</div>
      {worth && <div className="type-caption-strong mt-0.5 text-content">{worth}</div>}
      {rule && <div className="type-caption mt-2 leading-snug text-muted">{rule}</div>}
    </div>
  );
}

function PreviewPanel({ form }: { form: any }) {
  const [inputs, setInputs] = useState<any>({ prepaidAmount: 1000000, daysFromInvoice: 12, annualTurnover: 2500000, overdueAmount: 100000, daysOverdue: 30 });
  const [result, setResult] = useState<any>(null);
  const preview = useConfirmedMutation({ title: "Calculate the preview?", content: "It runs the scheme over real orders, which can take a moment.", okText: "Yes, calculate" }, {
    mutationFn: () => {
      const v = form.getFieldsValue();
      return request<any>({ url: '/discount-schemes/preview', method: 'POST', data: {
        advanceCdSlabs: (v.advanceCdSlabs ?? []).filter((s: any) => s?.minAmount != null),
        timelineCdSlabs: (v.timelineCdSlabs ?? []).filter((s: any) => s?.maxDays != null),
        todSlabs: (v.todSlabs ?? []).filter((s: any) => s?.fromAmount != null).map((s: any) => ({ ...s, toAmount: s.toAmount ?? null })),
        creditPeriodDays: v.creditPeriodDays ?? 0, overdueInterestPct: v.overdueInterestPct ?? 0, ...inputs,
      } });
    },
    onSuccess: (r) => setResult(r),
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Preview failed'),
  });

  const ex = result?.explain;
  const pct = (n: any) => (n == null ? '—' : `${n}%`);

  return (
    <Card className="mt-2"
      title={<span className="inline-flex items-center gap-2"><Calculator size={16} /> Preview calculator</span>}
      extra={<Button size="sm" variant="primary" loading={preview.isPending} onClick={() => preview.mutate()}>Calculate</Button>}>
      <p className="type-body mb-3 text-muted">
        Describe one retailer&rsquo;s behaviour below, and this works out what the rates you have entered above would
        give them. It is a what-if against the <b>unsaved</b> form — nothing here is stored or charged to anyone.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <ScenarioField label="Prepaid amount" hint="Paid up front, before dispatch">
          <InputNumber {...amountProps} value={inputs.prepaidAmount} onChange={(v) => setInputs({ ...inputs, prepaidAmount: v })} />
        </ScenarioField>
        <ScenarioField label="Days from invoice" hint="0 = paid at billing">
          <InputNumber style={{ width: '100%' }} min={0} value={inputs.daysFromInvoice} onChange={(v) => setInputs({ ...inputs, daysFromInvoice: v })} />
        </ScenarioField>
        <ScenarioField label="Annual turnover" hint="Their purchases for the year">
          <InputNumber {...amountProps} value={inputs.annualTurnover} onChange={(v) => setInputs({ ...inputs, annualTurnover: v })} />
        </ScenarioField>
        <ScenarioField label="Overdue amount" hint="Still unpaid after the credit period">
          <InputNumber {...amountProps} value={inputs.overdueAmount} onChange={(v) => setInputs({ ...inputs, overdueAmount: v })} />
        </ScenarioField>
        <ScenarioField label="Days overdue" hint="Days past the credit period">
          <InputNumber style={{ width: '100%' }} min={0} value={inputs.daysOverdue} onChange={(v) => setInputs({ ...inputs, daysOverdue: v })} />
        </ScenarioField>
      </div>

      {!result && (
        <p className="type-caption mt-3 text-subtle">Press <b>Calculate</b> to see what this retailer would earn.</p>
      )}

      {result && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <ResultCard
              name="Advance discount" jargon="Pays in full before dispatch"
              value={pct(result.advanceCdPct)} muted={!result.advanceCdPct}
              worth={ex?.advance?.amount ? `Saves ${inr(ex.advance.amount)} on this prepayment` : null}
              rule={ex?.advance?.rule}
            />
            <ResultCard
              name="Prompt-payment discount" jargon="Pays after delivery, but quickly"
              value={pct(result.timelineCdPct)} muted={!result.timelineCdPct}
              worth={result.timelineCdPct ? 'Applied to the invoice value when they pay' : null}
              rule={ex?.timeline?.rule}
            />
            <ResultCard
              name="Turnover discount" jargon="Year-end bonus on the year's purchases"
              value={pct(result.todPct)} muted={!result.todPct}
              worth={ex?.tod?.amount ? `Bonus of ${inr(ex.tod.amount)} at year end` : null}
              rule={ex?.tod?.rule}
            />
            <ResultCard
              name="Overdue interest" jargon="Charged to the retailer, not a discount"
              value={result.overdueInterest != null ? inr(result.overdueInterest) : '—'}
              muted={!result.overdueInterest}
              worth={result.overdueInterest ? 'Added to what they owe' : null}
              rule={ex?.overdue?.rule}
            />
          </div>

          {/* Only shown when the at-billing rate beats the advance slab — otherwise it just restates
              the advance card. This is the rate the live order flow actually applies. */}
          {ex?.beforeBilling?.differs && (
            <Alert type="info" className="mt-3"
              title={`Paying before dispatch actually earns ${ex.beforeBilling.pct}%, not ${result.advanceCdPct}%`}
              description={ex.beforeBilling.rule} />
          )}
        </>
      )}

      <p className="type-caption mt-3 mb-0 text-subtle">
        Indicative only — these figures are not yet applied to orders or invoices automatically (Phase 2).
      </p>
    </Card>
  );
}

// ─────────────────────────── Assign retailers ───────────────────────────

/** Page size for the assigned list — 20 across two columns is ten rows, which fits without scrolling. */
const ASSIGNED_PAGE = 20;

function AssignRetailersModal({ scheme, onClose, onChanged }: { scheme: any; onClose: () => void; onChanged: () => void }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [q, setQ] = useUrlString('aq');
  const [aState, setAState] = useUrlString('astate', ALL);
  const [aTerritory, setATerritory] = useState(ALL);
  const [page, setPage] = useUrlNumber('apage', 1);
  const open = !!scheme;

  const { data: full } = useQuery({
    queryKey: ['commercial-scheme', scheme?.id],
    queryFn: () => request<any>({ url: `/discount-schemes/${scheme.id}` }),
    enabled: open,
  });
  const { data: retailers } = useQuery({
    queryKey: ['retailers-picker'],
    queryFn: async () => {
      const r: any = await requestAllPages('/retailers');
      return Array.isArray(r) ? r : (r?.data ?? []);
    },
    enabled: open,
  });
  const { data: territories } = useQuery({
    queryKey: ['territories'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: open,
  });

  useEffect(() => { if (open) { setSelected([]); setQ(''); setAState(ALL); setATerritory(ALL); setPage(1); } }, [open]);

  const assign = useConfirmedMutation({ title: "Assign this scheme to the selected retailers?", content: "It starts applying to their orders from now on.", okText: "Yes, assign" }, {
    mutationFn: () => request({ url: `/discount-schemes/${scheme.id}/assign`, method: 'POST', data: {
      assignments: selected.map((rid) => ({ retailerId: rid })),
    } }),
    onSuccess: () => { message.success('Retailers assigned'); qc.invalidateQueries({ queryKey: ['commercial-scheme', scheme.id] }); setSelected([]); onChanged(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const removeOne = (retailerId: string) =>
    request({ url: `/discount-schemes/${scheme.id}/assign/${retailerId}`, method: 'DELETE' })
      .then(() => { qc.invalidateQueries({ queryKey: ['commercial-scheme', scheme.id] }); onChanged(); })
      .catch((e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to remove retailer'));

  const assignments = full?.assignments ?? [];
  const assignedIds = new Set(assignments.map((a: any) => String(a.retailerId)));

  // Clearing hundreds of rows one at a time was the only way out. This is destructive on a live scheme,
  // so it asks in a real dialog naming the scheme and the count — not a small popover.
  const removeAll = () => {
    Modal.confirm({
      type: 'warning',
      title: `Remove all ${assignments.length} retailers?`,
      content: `Every retailer comes off "${scheme.name}". Its cash discounts, credit terms and turnover discount then apply to nobody until you assign again.`,
      okText: `Remove all ${assignments.length}`,
      okType: 'danger',
      onOk: () => request<any>({ url: `/discount-schemes/${scheme.id}/assign`, method: 'DELETE' })
        .then((r: any) => {
          message.success(`Removed ${r?.removed ?? assignments.length} retailers`);
          setPage(1);
          qc.invalidateQueries({ queryKey: ['commercial-scheme', scheme.id] });
          onChanged();
        })
        .catch((e: any) => { message.error(e.response?.data?.error?.message ?? 'Failed to clear retailers'); throw e; }),
    });
  };

  const nameOf = (a: any) => String(a.retailerName ?? `#${a.retailerId}`);

  // An assignment row carries only the retailer's id and name, so state/beat are joined in from the
  // retailer directory this modal already loads for the picker — no extra request, and the assigned
  // list can then be narrowed the same way the picker is.
  const byId = useMemo(
    () => new Map<string, any>((retailers ?? []).map((r: any) => [String(r.id), r])),
    [retailers],
  );
  const terrName = useMemo(
    () => new Map<string, string>((territories ?? []).map((t: any) => [String(t.id), String(t.name ?? '')])),
    [territories],
  );
  const stateOf = (a: any) => String(byId.get(String(a.retailerId))?.stateCode ?? '');
  const territoryOf = (a: any) => {
    const tid = byId.get(String(a.retailerId))?.territoryId;
    return tid ? String(terrName.get(String(tid)) ?? '') : '';
  };

  // Offered from the ASSIGNED rows themselves, so every choice narrows to something.
  const assignedStateOptions = useMemo(() => {
    const codes = [...new Set(assignments.map(stateOf).filter(Boolean))] as string[];
    return [{ value: ALL, label: `All states (${assignments.length})` },
      ...codes.map((c) => ({ value: c, label: stateName(c) })).sort((a, b) => a.label.localeCompare(b.label))];
  }, [assignments, byId]);

  const assignedTerritoryOptions = useMemo(() => {
    const pool = assignments.filter((a: any) => aState === ALL || stateOf(a) === aState);
    const names = ([...new Set(pool.map(territoryOf).filter(Boolean))] as string[]).sort((a, b) => a.localeCompare(b));
    const label = aState === ALL ? `All territories (${names.length})` : `All in ${stateName(aState)} (${names.length})`;
    return [{ value: ALL, label }, ...names.map((n) => ({ value: n, label: n }))];
  }, [assignments, aState, byId, terrName]);

  // Choosing a state can strand a beat from a different one — fall back to all rather than to a
  // filter that matches nothing.
  useEffect(() => {
    if (aTerritory !== ALL && !assignedTerritoryOptions.some((o) => o.value === aTerritory)) setATerritory(ALL);
  }, [assignedTerritoryOptions, aTerritory]);

  const needle = q.trim().toLowerCase();
  const filtered = assignments.filter((a: any) =>
    (!needle || nameOf(a).toLowerCase().includes(needle))
    && (aState === ALL || stateOf(a) === aState)
    && (aTerritory === ALL || territoryOf(a) === aTerritory));
  const pageRows = filtered.slice((page - 1) * ASSIGNED_PAGE, page * ASSIGNED_PAGE);

  return (
    <Modal
      // A centred modal like the scheme form it is opened from — this was the last side-panel on the
      // page, and the inconsistency showed: one dialog slid in from the right, its sibling opened in
      // the middle.
      open={open} onOpenChange={(o) => { if (!o) onClose(); }} width={980}
      title={scheme ? `Retailers · ${scheme.name}` : ''}
      footer={<Button variant="default" onClick={onClose}>Close</Button>}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Card title="Assign retailers">
          {/* Same picker as the scheme form: state/territory narrowing + bulk select. Retailers already
              on the scheme are excluded here — they are managed in the list below. */}
          <RetailerPicker
            retailers={(retailers ?? []).filter((r: any) => !assignedIds.has(String(r.id)))}
            value={selected} onChange={setSelected}
          />
          {/* The workflow is deliberately just: create the scheme, assign retailers. There is no
              acceptance step, so assignment IS the action and nothing else is offered. */}
          <div className="mt-3">
            <Button variant="primary" disabled={!selected.length} loading={assign.isPending} onClick={() => assign.mutate()}>
              Assign {selected.length || ''}
            </Button>
          </div>
        </Card>

        <Card
          title={`Assigned retailers (${assignments.length})`}
          extra={assignments.length > 0 ? (
            <Button variant="text" size="sm" className="text-danger-600!" icon={<Trash2 size={16} />} onClick={removeAll}>
              Remove all
            </Button>
          ) : undefined}
        >
          {assignments.length === 0 ? <EmptyState title="No retailers assigned" /> : (
            <>
              {assignments.length > ASSIGNED_PAGE && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Select showSearch value={aState} onChange={(v: any) => { setAState(v ?? ALL); setPage(1); }}
                    options={assignedStateOptions} style={{ width: 190 }} aria-label="Filter assigned retailers by state" />
                  <Select showSearch value={aTerritory} onChange={(v: any) => { setATerritory(v ?? ALL); setPage(1); }}
                    options={assignedTerritoryOptions} style={{ width: 230 }} aria-label="Filter assigned retailers by territory" />
                  <Input allowClear className="min-w-[200px] flex-1" placeholder="Find an assigned retailer…"
                    value={q} onChange={(e: any) => { setQ(e.target.value); setPage(1); }} />
                  {(aState !== ALL || aTerritory !== ALL || q) && (
                    <span className="type-caption text-subtle">{filtered.length} of {assignments.length}</span>
                  )}
                </div>
              )}
              {/* Two columns and compact type — a scheme routinely carries a few hundred retailers, and a
                  single tall list meant endless scrolling. Two columns ONLY here: this is a dense
                  reference list, not a form. */}
              <div className="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
                {pageRows.map((a: any) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 border-b border-line/70 py-1">
                    <span className="min-w-0 flex-1 truncate type-caption text-content" title={nameOf(a)}>{nameOf(a)}</span>
                    <span className="flex shrink-0 items-center">
                      <Button variant="text" size="sm" icon={<FileText size={15} />} title="Scheme PDF for this retailer"
                        aria-label="Scheme PDF for this retailer"
                        onClick={() => openSchemePdf(scheme.id, String(a.retailerId)).catch(() => message.error('Failed to open PDF'))} />
                      <Popconfirm title="Remove this retailer?" onConfirm={() => removeOne(String(a.retailerId))}>
                        <Button variant="text" size="sm" className="text-danger-600!" icon={<Trash2 size={15} />} aria-label="Remove retailer" />
                      </Popconfirm>
                    </span>
                  </div>
                ))}
              </div>
              {filtered.length === 0 && <p className="type-caption py-3 text-subtle">No assigned retailer matches that search.</p>}
              {filtered.length > ASSIGNED_PAGE && (
                <div className="mt-3 flex justify-end">
                  <Pagination size="small" current={page} pageSize={ASSIGNED_PAGE} total={filtered.length}
                    onChange={setPage}
                    showTotal={(t: number, r: [number, number]) => <span className="type-caption text-subtle">{r[0]}–{r[1]} of {t}</span>} />
                </div>
              )}
            </>
          )}
        </Card>
      </Space>
    </Modal>
  );
}
